<template lang="pug">
div(v-if="hasAccess")
  server-data(
    v-model="userDuplicates"
    :method="getUserDuplicates"
    :serialize-data-items="serializeUserDuplicatesItems"
    :start-loading-value="!hasInitData"
    :no-init="hasInitData"
    @data:init="onUserDuplicateInit"
  )
    template(v-if="currentDuplicate")
      portal(
        :to="`${constants.DIALOG_PORTAL_KEY}.content`"
        v-slot="{ close }"
      )
        .d-flex.flex-column.gap-8.text-justify
          p(v-if="!hasInitData") Мы нашли пользователя системы, данные которого подозрительно похожи на ваши.&nbsp;
            | Существует учетная запись для данных {{ currentDuplicate.other_user.profile.public_name }},&nbsp;
            | для регистрации которой использовался {{ currentSource.name }}. Если это тоже вы - пожалуйста,&nbsp;
            | объедините свои учетные записи.
          template(v-if="currentMergeData")
            component(
              :key="currentSource.id"
              :is="currentMergeData.component"
              v-bind="currentMergeData.attrs"
              v-on="currentMergeData.listeners(close)"
            )
      portal(
        :to="`${constants.DIALOG_PORTAL_KEY}.actions`"
        v-slot="{ close }"
      )
        .d-flex.gap-16.flex-wrap.full-width
          app-button(
            rounded
            outlined
            large
            @click="onClose(close)"
          ) Закрыть
          template(v-if="currentDuplicate.status.id === constants.USER_DUPLICATE_STATUS_KEY.notProceed")
            app-button(
              color="redesignError"
              rounded
              large
              @click="noDuplicate(close)"
            ) Это не я, ничего не нужно объединять
          app-button(
            v-if="!hasInitData"
            rounded
            large
            @click="onHideNextTime(close)"
          ) Не показывать больше
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import MergeEmail from './MergeEmail.vue';
import MergeSocial from './MergeSocial.vue';
import AppButton from '@/components/user-redesign/controls/AppButton.vue';
import UserDuplicate from '@/models/list/user-duplicate';
import { dateFnsFormat } from '@/helpers/dateFns';
import {
  USER_DUPLICATE_STATUS_KEY,
  USER_DUPLICATE_SOURCE_KEY,
  USER_DUPLICATE_SOURCE_MAP,
  USER_DUPLICATES_LOCAL_STORAGE_KEY,
} from './constants';
import uniqueId from 'lodash/uniqueId';

const DIALOG_PORTAL_KEY = uniqueId('user-duplicates-dialog');

const DEFAULT_STATE = {
  currentIndex: 0,
  userDuplicates: [],
};

const HIDE_FOR_ROUTES = ['auth-redirect-vk', 'auth-redirect-apple', 'auth-redirect-telegram'];

export default {
  name: 'UserDuplicatesDialog',
  components: {
    MergeEmail,
    MergeSocial,
    AppButton,
  },
  props: {
    initData: {
      type: Object, // { userDuplicates, currentSource }
      default: null,
    },
  },
  filters: {
    readableDate: dateFnsFormat.readableDate,
  },
  data() {
    return {
      ...DEFAULT_STATE,
    };
  },
  computed: {
    ...mapGetters({
      hideUserDuplicateDialogFor: 'auth/getHideUserDuplicateDialogFor',
    }),
    constants() {
      return {
        DIALOG_PORTAL_KEY,
        USER_DUPLICATE_SOURCE_KEY,
        USER_DUPLICATE_STATUS_KEY,
      };
    },
    hasInitData() {
      return !!Object.values(this.initData || {})?.length;
    },
    hasAccess() {
      return this.$user.isAuthenticated && !!this.$user.hasRoles([this.$conf.ROLES.certificate, this.$conf.ROLES.member]);
    },
    currentDuplicate() {
      return this.userDuplicates[this.currentIndex];
    },
    currentSource() {
      if (this.hasInitData && this.initData?.currentSource) return USER_DUPLICATE_SOURCE_MAP[this.initData.currentSource];
      return Object.values(USER_DUPLICATE_SOURCE_MAP).find(({ backendField }) => !!this.currentDuplicate[backendField]);
    },
    currentMergeData() {
      const MERGE_SOCIAL_PROPS = {
        component: MergeSocial,
        attrs: {
          source: this.currentSource,
        },
        listeners: (closeDialogCb) => ({
          'click:init': () => this.initMerge(closeDialogCb),
        }),
      };

      const map = {
        [USER_DUPLICATE_SOURCE_KEY.email]: {
          component: MergeEmail,
          attrs: {
            duplicate: this.currentDuplicate,
            source: this.currentSource,
          },
          listeners: (closeDialogCb) => ({
            'click:init': (data) => this.initMergeEmail(data, closeDialogCb),
          }),
        },
        [USER_DUPLICATE_SOURCE_KEY.vk]: MERGE_SOCIAL_PROPS,
        [USER_DUPLICATE_SOURCE_KEY.telegram]: MERGE_SOCIAL_PROPS,
        [USER_DUPLICATE_SOURCE_KEY.apple]: MERGE_SOCIAL_PROPS,
      };

      return map[this.currentSource?.id] || null;
    },
    isHideByUser() {
      return !!this.hideUserDuplicateDialogFor.includes(this.$user?.data?.id);
    },
  },
  watch: {
    '$user.isAuthenticated': {
      handler(value) {
        if (!value) {
          this.resetState();
        }
      },
    },
  },
  created() {
    this.setInitData();
  },
  methods: {
    ...mapActions({
      hideUserDuplicateDialogForUser: 'auth/hideUserDuplicateDialogForUser',
    }),
    setInitData() {
      if (this.hasInitData) {
        this.currentIndex = 0;
        this.userDuplicates = this.initData.userDuplicates || [];
        this.openDialog();
      }
    },
    resetState() {
      Object.entries(DEFAULT_STATE).forEach(([key, value]) => {
        this[key] = value;
      });
    },
    openDialog() {
      this.$dialogs.portal({
        attrs: {
          'max-width': 800,
          title: 'Дубликат учетной записи',
          inputValue: DIALOG_PORTAL_KEY,
          persistent: true,
          scrollable: false,
        },
        listeners: {
          close: () => {
            this.$emit('close');
          },
        },
      });
    },
    getUserDuplicates() {
      const params = {
        'search[users_exists]': 1,
        'search[status]': [USER_DUPLICATE_STATUS_KEY.notProceed, USER_DUPLICATE_STATUS_KEY.mergeInitiate].join(','),
        expand: 'other_user,user_duplicate_merges',
      };

      return this.$rApi.userDuplicates.getUserDuplicates({ params });
    },
    serializeUserDuplicatesItems(data) {
      const checkFields = Object.values(USER_DUPLICATE_SOURCE_MAP).map(({ backendField }) => backendField);
      return data.map((userDuplicate) => new UserDuplicate(userDuplicate)).filter((item) => checkFields.map((field) => item[field]).some(Boolean));
    },
    onUserDuplicateInit(data) {
      if (!data.length || !this.$user.isAuthenticated || HIDE_FOR_ROUTES.includes(this.$route.name) || this.isHideByUser) {
        return;
      }

      this.openDialog();
    },
    onClose(closeDialogCb) {
      if (closeDialogCb) {
        closeDialogCb();
      }

      const nextIndex = this.currentIndex + 1;
      if (this.userDuplicates[nextIndex]) {
        this.currentIndex += nextIndex;
        this.openDialog();
      } else {
        this.resetState();
      }
    },
    onHideNextTime(closeDialogCb) {
      this.hideUserDuplicateDialogForUser(this.$user.data.id);
      closeDialogCb();
    },
    async initMerge(closeDialogCb) {
      const { method, id } = this.currentSource;

      const { response, error } = await method();
      if (error) {
        return;
      }

      const payload = {
        duplicateId: this.currentDuplicate.id,
        sourceId: id,
      };

      localStorage.setItem(USER_DUPLICATES_LOCAL_STORAGE_KEY, JSON.stringify(payload));

      const { uri } = response.data.data;

      const { name, path } = this.$route;
      if (name !== 'login') {
        localStorage.setItem('redirect', path);
      }

      if (uri) {
        this.onClose(closeDialogCb);
        window.location.href = uri;
      }
    },
    async initMergeEmail({ data }, closeDialogCb) {
      const { id } = this.currentDuplicate;

      const args = [id];

      const { error } = await this.$rApi.userDuplicates.confirmMerge({ args, data });
      if (error) {
        return;
      }

      const payload = {
        duplicateId: id,
        sourceId: this.currentSource.id,
      };

      localStorage.setItem(USER_DUPLICATES_LOCAL_STORAGE_KEY, JSON.stringify(payload));
      this.$toast.success('Письмо отправлено на почту');
      this.onClose(closeDialogCb);
    },
    async noDuplicate(closeDialogCb) {
      const args = [this.currentDuplicate.id];

      const { error } = await this.$rApi.userDuplicates.noDuplicate({ args });
      if (error) {
        return;
      }

      this.$toast.success('Вы больше не будете получать уведомление об этом дубликате учетной записи');
      this.onClose(closeDialogCb);
    },
  },
};
</script>
